import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, Package, CreditCard, MapPin } from 'lucide-react';
import { equipmentAPI, equipmentBookingAPI, getImageUrl, userAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import { format, addDays, differenceInDays } from 'date-fns';

const EquipmentBookingFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [guests, setGuests] = useState([]);
  const [form, setForm] = useState({
    startDate: format(new Date(), 'yyyy-MM-dd'),
    endDate: format(addDays(new Date(), 1), 'yyyy-MM-dd'),
    quantity: 1,
    pickupLocation: '',
    propertyBookingId: '',
    guestId: ''
  });
  const [estimatedTotal, setEstimatedTotal] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [itemRes, guestsRes] = await Promise.all([
          equipmentAPI.getOne(id),
          userAPI.getAll({ role: 'guest', limit: 100 })
        ]);
        setItem(itemRes.data.data);
        setGuests(guestsRes.data.data || []);
        calculateTotal(itemRes.data.data, form);
      } catch {
        toast.error('Failed to load equipment');
        navigate('/equipment');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const calculateTotal = (equipment, formData) => {
    if (!equipment) return;
    const start = new Date(formData.startDate);
    const end = new Date(formData.endDate);
    const days = Math.max(1, differenceInDays(end, start));
    const subtotal = equipment.pricing.dailyRate * days * formData.quantity;
    const taxes = Math.round(subtotal * 0.1);
    const total = subtotal + taxes + (equipment.pricing.securityDeposit || 0);
    setEstimatedTotal(total);
  };

  const handleFormChange = (field, value) => {
    const newForm = { ...form, [field]: value };
    setForm(newForm);
    if (item) calculateTotal(item, newForm);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!item) return;

    const start = new Date(form.startDate);
    const end = new Date(form.endDate);
    if (end <= start) {
      toast.error('End date must be after start date');
      return;
    }

    if (item.stock.available < form.quantity) {
      toast.error(`Only ${item.stock.available} items available`);
      return;
    }

    setSubmitting(true);
    try {
      const res = await equipmentBookingAPI.create({
        equipmentId: id,
        startDate: form.startDate,
        endDate: form.endDate,
        quantity: form.quantity,
        pickupLocation: form.pickupLocation,
        propertyBookingId: form.propertyBookingId || undefined,
        guestId: form.guestId || undefined
      });

      if (res.data.data.clientSecret) {
        // Redirect to payment or show payment modal
        toast.success('Booking created! Proceeding to payment...');
        // For now, just navigate to bookings
        navigate('/equipment/bookings');
      } else {
        toast.success('Booking created successfully!');
        navigate('/equipment/bookings');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Booking failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="loading-center"><div className="spinner spinner-lg" /></div>;
  if (!item) return <div className="empty-state"><h3>Equipment not found</h3></div>;

  const days = Math.max(1, differenceInDays(new Date(form.endDate), new Date(form.startDate)));
  const subtotal = item.pricing.dailyRate * days * form.quantity;
  const taxes = Math.round(subtotal * 0.1);
  const securityDeposit = item.pricing.securityDeposit || 0;

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn btn-ghost btn-icon" onClick={() => navigate(`/equipment/${id}`)}>
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="page-title">Book Equipment</h1>
            <p className="page-subtitle">{item.name}</p>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 24 }}>
        <div className="card">
          <div className="card-header"><span className="card-title">Booking Details</span></div>
          <form onSubmit={handleSubmit}>
            <div className="form-grid form-grid-2">
              <div className="form-group">
                <label className="form-label">Start Date</label>
                <input
                  type="date"
                  className="form-input"
                  value={form.startDate}
                  onChange={(e) => handleFormChange('startDate', e.target.value)}
                  min={format(new Date(), 'yyyy-MM-dd')}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">End Date</label>
                <input
                  type="date"
                  className="form-input"
                  value={form.endDate}
                  onChange={(e) => handleFormChange('endDate', e.target.value)}
                  min={form.startDate}
                  required
                />
              </div>
            </div>

            <div className="form-group" style={{ marginTop: 16 }}>
              <label className="form-label">Guest (Optional)</label>
              <select
                className="form-select"
                value={form.guestId}
                onChange={(e) => handleFormChange('guestId', e.target.value)}
              >
                <option value="">Book for yourself</option>
                {guests.map(g => (
                  <option key={g._id} value={g._id}>
                    {g.firstName} {g.lastName} ({g.email})
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group" style={{ marginTop: 16 }}>
              <label className="form-label">Quantity</label>
              <input
                type="number"
                className="form-input"
                min={1}
                max={item.stock.available}
                value={form.quantity}
                onChange={(e) => handleFormChange('quantity', parseInt(e.target.value) || 1)}
                required
              />
              <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>
                Available: {item.stock.available} units
              </div>
            </div>

            <div className="form-group" style={{ marginTop: 16 }}>
              <label className="form-label">Pickup Location</label>
              <input
                type="text"
                className="form-input"
                placeholder="e.g., Front desk, Rental office"
                value={form.pickupLocation}
                onChange={(e) => handleFormChange('pickupLocation', e.target.value)}
                required
              />
            </div>

            <div style={{ marginTop: 24, display: 'flex', gap: 12 }}>
              <button type="button" className="btn btn-secondary" onClick={() => navigate(`/equipment/${id}`)}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={submitting || item.stock.available < form.quantity}>
                {submitting ? 'Processing...' : `Book Now - $${estimatedTotal.toLocaleString()}`}
              </button>
            </div>
          </form>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="card">
            <div className="card-header"><span className="card-title">Equipment</span></div>
            {item.images?.[0]?.url && (
              <img
                src={getImageUrl(item.images[0].url)}
                alt={item.name}
                style={{ width: '100%', height: 160, objectFit: 'cover', borderRadius: 8, marginBottom: 12 }}
              />
            )}
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>{item.name}</h3>
            <p style={{ fontSize: 13, color: '#6B7280', marginBottom: 12 }}>{item.category?.replace('_', ' ')}</p>
            {item.description && (
              <p style={{ fontSize: 13, color: '#374151', lineHeight: 1.5 }}>{item.description}</p>
            )}
          </div>

          <div className="card">
            <div className="card-header"><span className="card-title">Price Breakdown</span></div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
                <span style={{ color: '#6B7280' }}>${item.pricing.dailyRate} × {days} day(s) × {form.quantity}</span>
                <span>${subtotal.toLocaleString()}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
                <span style={{ color: '#6B7280' }}>Taxes (10%)</span>
                <span>${taxes.toLocaleString()}</span>
              </div>
              {securityDeposit > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
                  <span style={{ color: '#6B7280' }}>Security Deposit</span>
                  <span>${securityDeposit.toLocaleString()}</span>
                </div>
              )}
              <div style={{ height: 1, background: '#E5E7EB', margin: '8px 0' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 16, fontWeight: 700 }}>
                <span>Total</span>
                <span>${estimatedTotal.toLocaleString()}</span>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header"><span className="card-title">Requirements</span></div>
            <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.6 }}>
              {item.requirements?.minimumAge && <div style={{ marginBottom: 4 }}>• Minimum age: {item.requirements.minimumAge}+</div>}
              {item.requirements?.licenseRequired && <div style={{ marginBottom: 4 }}>• Valid license required</div>}
              {item.requirements?.idRequired && <div style={{ marginBottom: 4 }}>• Valid ID required</div>}
              {item.requirements?.depositRequired && <div style={{ marginBottom: 4 }}>• Security deposit required</div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EquipmentBookingFormPage;
